/**
 * Error Handling & Monitoring Utilities
 * PHASE 5: Structured Error Handling, Performance Monitoring, and Health Checks
 * 
 * Provides comprehensive error handling, performance tracking, and system health monitoring
 */

// ============================================================================
// Custom Error Classes
// ============================================================================

/**
 * Base Firestore Error class with structured data
 */
export class FirestoreError extends Error {
  constructor(message, code, operation, data = {}) {
    super(message);
    this.name = 'FirestoreError';
    this.code = code;
    this.operation = operation;
    this.data = data;
    this.timestamp = new Date().toISOString();
  }
  
  /**
   * Convert error to structured log format
   */
  toLogFormat() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      operation: this.operation,
      data: this.data,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }
  
  /**
   * Get user-friendly error message
   */
  getUserMessage() {
    switch (this.code) {
      case 'permission-denied':
        return 'You do not have permission to perform this action';
      case 'not-found':
        return 'The requested resource was not found';
      case 'already-exists':
        return 'This resource already exists';
      case 'unavailable':
        return 'Service temporarily unavailable. Please try again in a moment.';
      case 'deadline-exceeded':
        return 'Request timeout. Please check your connection and try again.';
      case 'resource-exhausted':
        return 'Too many requests. Please wait a moment and try again.';
      case 'unauthenticated':
        return 'Please sign in to continue';
      case 'cancelled':
        return 'Operation cancelled';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
}

/**
 * Validation Error for schema validation failures
 */
export class ValidationError extends Error {
  constructor(message, field, errors = []) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.errors = errors;
    this.timestamp = new Date().toISOString();
  }
  
  toLogFormat() {
    return {
      name: this.name,
      message: this.message,
      field: this.field,
      errors: this.errors,
      timestamp: this.timestamp
    };
  }
}

// ============================================================================
// Error Handler
// ============================================================================

/**
 * Handle Firestore errors with logging and user feedback
 * @param {Error} error - The error object
 * @param {string} operation - Operation being performed (e.g., 'create-customer')
 * @param {Object} context - Additional context data
 * @returns {string} User-friendly error message
 */
export function handleFirestoreError(error, operation, context = {}) {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error(`[Firestore Error] ${operation}:`, error);
    if (Object.keys(context).length > 0) {
      console.error('[Context]:', context);
    }
  }
  
  // Send to analytics if available
  if (window.gtag) {
    window.gtag('event', 'firestore_error', {
      error_code: error.code || 'unknown',
      error_message: error.message,
      operation: operation,
      timestamp: new Date().toISOString()
    });
  }
  
  // Create structured error if not already
  const structuredError = error instanceof FirestoreError
    ? error
    : new FirestoreError(error.message, error.code, operation, context);
  
  // Log to error tracking service (if configured)
  logErrorToService(structuredError);
  
  // Return user-friendly message
  return structuredError.getUserMessage();
}

/**
 * Handle validation errors
 * @param {Array} errors - Array of validation error messages
 * @param {string} entityType - Type of entity being validated
 * @returns {string} Formatted error message
 */
export function handleValidationError(errors, entityType) {
  const error = new ValidationError(
    `Validation failed for ${entityType}`,
    entityType,
    errors
  );
  
  if (process.env.NODE_ENV === 'development') {
    console.error('[Validation Error]:', error.toLogFormat());
  }
  
  if (window.gtag) {
    window.gtag('event', 'validation_error', {
      entity_type: entityType,
      error_count: errors.length,
      timestamp: new Date().toISOString()
    });
  }
  
  // Return first error or generic message
  return errors.length > 0 
    ? `Validation error: ${errors[0]}`
    : 'Please check your input and try again';
}

/**
 * Log error to external service (placeholder for future integration)
 */
function logErrorToService(error) {
  // TODO: Integrate with error tracking service (Sentry, LogRocket, etc.)
  // For now, just store in sessionStorage for debugging
  try {
    const errors = JSON.parse(sessionStorage.getItem('app_errors') || '[]');
    errors.push(error.toLogFormat());
    // Keep only last 50 errors
    if (errors.length > 50) {
      errors.shift();
    }
    sessionStorage.setItem('app_errors', JSON.stringify(errors));
  } catch (e) {
    // Ignore storage errors
  }
}

// ============================================================================
// Performance Monitor
// ============================================================================

export class PerformanceMonitor {
  /**
   * Track query performance
   * @param {string} queryName - Name of the query
   * @param {Function} queryFn - Async function that performs the query
   * @returns {Promise} Query result
   */
  static async trackQuery(queryName, queryFn) {
    const start = performance.now();
    const startMark = `query-start-${queryName}`;
    const endMark = `query-end-${queryName}`;
    
    // Performance API marks for better DevTools integration
    try {
      performance.mark(startMark);
    } catch (e) {
      // Ignore if marks API not available
    }
    
    try {
      const result = await queryFn();
      const duration = performance.now() - start;
      
      // End performance mark
      try {
        performance.mark(endMark);
        performance.measure(`query-${queryName}`, startMark, endMark);
      } catch (e) {
        // Ignore
      }
      
      // Log metrics
      this.logMetric('query_performance', {
        query_name: queryName,
        duration_ms: Math.round(duration),
        result_count: result?.docs?.length || result?.length || 0,
        status: 'success'
      });
      
      // Warn on slow queries
      if (duration > 2000) {
        console.warn(`[Performance] Slow query detected: ${queryName} took ${duration.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      
      // Log error metrics
      this.logMetric('query_error', {
        query_name: queryName,
        duration_ms: Math.round(duration),
        error_code: error.code || 'unknown',
        status: 'error'
      });
      
      throw error;
    }
  }
  
  /**
   * Track operation performance (create, update, delete)
   * @param {string} operationName - Name of the operation
   * @param {Function} operationFn - Async function that performs the operation
   * @returns {Promise} Operation result
   */
  static async trackOperation(operationName, operationFn) {
    const start = performance.now();
    
    try {
      const result = await operationFn();
      const duration = performance.now() - start;
      
      this.logMetric('operation_performance', {
        operation_name: operationName,
        duration_ms: Math.round(duration),
        status: 'success'
      });
      
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      
      this.logMetric('operation_error', {
        operation_name: operationName,
        duration_ms: Math.round(duration),
        error_code: error.code || 'unknown',
        status: 'error'
      });
      
      throw error;
    }
  }
  
  /**
   * Start tracking an operation (returns ID for later completion)
   * Use this for manual tracking when you can't wrap in a function
   * @param {string} operationName - Name of the operation
   * @returns {string} Tracking ID
   */
  static startOperation(operationName) {
    const id = `${operationName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const start = performance.now();
    
    // Store start time
    this._operations = this._operations || new Map();
    this._operations.set(id, { name: operationName, start });
    
    return id;
  }
  
  /**
   * End tracking an operation started with startOperation()
   * @param {string} id - Tracking ID returned by startOperation()
   * @param {boolean} success - Whether operation succeeded
   */
  static endOperation(id, success = true) {
    this._operations = this._operations || new Map();
    const operation = this._operations.get(id);
    
    if (!operation) {
      console.warn('[PerformanceMonitor] No operation found for ID:', id);
      return;
    }
    
    const duration = performance.now() - operation.start;
    
    this.logMetric(success ? 'operation_performance' : 'operation_error', {
      operation_name: operation.name,
      duration_ms: Math.round(duration),
      status: success ? 'success' : 'error'
    });
    
    this._operations.delete(id);
  }
  
  /**
   * Log performance metric
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  static logMetric(event, data) {
    // Send to Google Analytics if available
    if (window.gtag && process.env.NODE_ENV === 'production') {
      window.gtag('event', event, data);
    }
    
    // Log to console in development - only errors or slow operations (>2000ms)
    if (process.env.NODE_ENV === 'development') {
      if (data.status === 'error') {
        console.error(`[Performance] ❌ ${event}:`, data);
      } else if (data.duration_ms > 2000) {
        console.warn(`[Performance] ⚠️ Slow operation ${event}:`, data);
      }
    }
    
    // Store metrics for later analysis
    this.storeMetric(event, data);
  }
  
  /**
   * Store metric in sessionStorage for debugging
   */
  static storeMetric(event, data) {
    try {
      const metrics = JSON.parse(sessionStorage.getItem('app_metrics') || '[]');
      metrics.push({
        event,
        data,
        timestamp: new Date().toISOString()
      });
      
      // Keep only last 100 metrics
      if (metrics.length > 100) {
        metrics.shift();
      }
      
      sessionStorage.setItem('app_metrics', JSON.stringify(metrics));
    } catch (e) {
      // Ignore storage errors
    }
  }
  
  /**
   * Get stored metrics for analysis
   * @returns {Array} Array of metrics
   */
  static getMetrics() {
    try {
      return JSON.parse(sessionStorage.getItem('app_metrics') || '[]');
    } catch (e) {
      return [];
    }
  }
  
  /**
   * Clear stored metrics
   */
  static clearMetrics() {
    try {
      sessionStorage.removeItem('app_metrics');
    } catch (e) {
      // Ignore
    }
  }
}

// ============================================================================
// Health Check System
// ============================================================================

export class FirestoreHealthChecker {
  /**
   * Check Firestore connection health
   * @param {Object} db - Firestore instance
   * @returns {Promise<Object>} Health status
   */
  static async checkConnection(db) {
    const start = performance.now();
    
    try {
      // Import dynamically to avoid circular dependencies
      const { collection, query, limit, getDocs } = await import('firebase/firestore');
      const { collectionPath } = await import('./firestoreCollections');
      
      // Try to read from users collection (authenticated users should have access)
      // Use a simple query with limit to minimize data transfer
      const usersQuery = query(collection(db, collectionPath('users')), limit(1));
      await getDocs(usersQuery);
      
      const duration = performance.now() - start;
      
      return {
        status: 'healthy',
        responseTime: Math.round(duration),
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const duration = performance.now() - start;
      
      // If permission-denied, the connection itself is fine, just auth issue
      // Don't treat as unhealthy in production
      if (error.code === 'permission-denied') {
        return {
          status: 'auth-required',
          error: error.code,
          errorMessage: 'Authentication required for health check',
          responseTime: Math.round(duration),
          timestamp: new Date().toISOString()
        };
      }
      
      return {
        status: 'unhealthy',
        error: error.code || 'unknown',
        errorMessage: error.message,
        responseTime: Math.round(duration),
        timestamp: new Date().toISOString()
      };
    }
  }
  
  /**
   * Run periodic health checks
   * @param {Object} db - Firestore instance
   * @param {number} intervalMs - Interval in milliseconds (default: 60000 = 1 minute)
   * @param {Function} onUnhealthy - Callback when health check fails
   * @returns {Function} Stop function to cancel periodic checks
   */
  static runPeriodicCheck(db, intervalMs = 60000, onUnhealthy = null) {
    const checkHealth = async () => {
      const health = await this.checkConnection(db);
      
      // Only log if truly unhealthy (not auth-required)
      if (health.status === 'unhealthy') {
        console.error('[Health Check] Firestore connection unhealthy:', health);
        
        // Call callback if provided
        if (onUnhealthy && typeof onUnhealthy === 'function') {
          onUnhealthy(health);
        }
        
        // Send to analytics
        if (window.gtag) {
          window.gtag('event', 'health_check_failed', {
            error: health.error,
            response_time: health.responseTime
          });
        }
      } else if (health.status === 'auth-required') {
        // Auth required is not a true health issue, silent in all modes
      } else if (process.env.NODE_ENV === 'development' && health.responseTime > 1000) {
        // Only log if response time is slow (>1000ms)
        console.warn('[Health Check] ⚠️ Slow Firestore response:', health.responseTime, 'ms');
      }
      // Silent for healthy fast responses
    };
    
    // Initial check
    checkHealth();
    
    // Periodic checks
    const intervalId = setInterval(checkHealth, intervalMs);
    
    // Return stop function
    return () => clearInterval(intervalId);
  }
  
  /**
   * Check cache status
   * @returns {Object} Cache status
   */
  static async checkCacheStatus() {
    try {
      // Check if IndexedDB is available
      if (!window.indexedDB) {
        return {
          available: false,
          reason: 'IndexedDB not supported'
        };
      }
      
      // Check storage quota
      const estimate = await navigator.storage?.estimate?.();
      
      return {
        available: true,
        usage: estimate?.usage || 0,
        quota: estimate?.quota || 0,
        usagePercent: estimate?.usage && estimate?.quota
          ? Math.round((estimate.usage / estimate.quota) * 100)
          : 0
      };
    } catch (error) {
      return {
        available: false,
        reason: error.message
      };
    }
  }
}

// ============================================================================
// Diagnostics
// ============================================================================

/**
 * Get diagnostic report for troubleshooting
 * @param {Object} db - Firestore instance
 * @returns {Promise<Object>} Diagnostic report
 */
export async function getDiagnostics(db) {
  const metrics = PerformanceMonitor.getMetrics();
  const errors = JSON.parse(sessionStorage.getItem('app_errors') || '[]');
  const health = await FirestoreHealthChecker.checkConnection(db);
  const cache = await FirestoreHealthChecker.checkCacheStatus();
  
  // Calculate average query time
  const queryMetrics = metrics.filter(m => m.event === 'query_performance');
  const avgQueryTime = queryMetrics.length > 0
    ? Math.round(
        queryMetrics.reduce((sum, m) => sum + m.data.duration_ms, 0) / queryMetrics.length
      )
    : 0;
  
  // Count errors by type
  const errorsByType = errors.reduce((acc, err) => {
    const code = err.code || 'unknown';
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});
  
  return {
    timestamp: new Date().toISOString(),
    health,
    cache,
    metrics: {
      total: metrics.length,
      queries: queryMetrics.length,
      avgQueryTime,
      slowQueries: queryMetrics.filter(m => m.data.duration_ms > 2000).length
    },
    errors: {
      total: errors.length,
      byType: errorsByType,
      recent: errors.slice(-5) // Last 5 errors
    },
    browser: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled
    }
  };
}

/**
 * Export diagnostics as JSON for support
 * @param {Object} db - Firestore instance
 * @returns {Promise<string>} JSON string of diagnostics
 */
export async function exportDiagnostics(db) {
  const diagnostics = await getDiagnostics(db);
  return JSON.stringify(diagnostics, null, 2);
}

/**
 * Clear all stored diagnostics data
 */
export function clearDiagnostics() {
  try {
    sessionStorage.removeItem('app_metrics');
    sessionStorage.removeItem('app_errors');
    console.log('[Diagnostics] Cleared all diagnostic data');
  } catch (e) {
    console.error('[Diagnostics] Failed to clear data:', e);
  }
}

